import { memo, useState, useCallback, useEffect, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import { KstDataGrid } from '@/components/KstDataGrid';
import { useTheme } from '@/contexts/ThemeContext';
import { AdvisoryAckNamesModal } from '@/components/screens/advisory/AdvisoryAckNamesModal';
import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
import { getOfficerLabel, getOfficerPluralLabel } from '@/utils/auth';
import moment from 'moment';

import type {
  KstDataGridColumn,
  KstDataGridPager,
  KstDataGridRows,
} from '@/components/KstDataGrid';

// ============================================================================
// AdvisoryReviewStatusTab — supervisor "did our officers see the BOLO?" view.
// ----------------------------------------------------------------------------
// Third tab on Advisory Management, gated by the SAME permission as the Audit
// Log tab (ROLE_ORNA_ADVISORY_AUDIT_LOG). One row per company person holding
// ROLE_ORNA_ADVISORY_READ. Lazy-loaded on first visibility like the audit tab.
// Data contract: ADVISORY_RECS_IMPLEMENTATION_SPEC §4.5.
// ============================================================================

interface OutstandingUrgent {
  advisoryId: number;
  displayId: number;
  subject: string;
}

interface ReviewStatusRow {
  personId: number;
  firstName: string;
  lastName: string;
  groupNames: string[];
  lastReviewedAt: string | null;
  stale: boolean;
  outstandingUrgent: OutstandingUrgent[];
}

// In-slot ACTIVE urgent advisories, newest first, with fresh-ack progress. Fed
// by the reviewstatus response's urgentAdvisories block.
interface UrgentAdvisorySummary {
  advisoryId: number;
  displayId: number;
  subject: string;
  acknowledgedCount: number;
  rosterCount: number;
}

export interface AdvisoryReviewStatusTabHandle {
  refresh: () => void;
}

interface AdvisoryReviewStatusTabProps {
  isVisible: boolean;
}

// Effectively "show all", matching User Management: the API returns the whole
// roster in one response and supervisors scan it, so paging only gets in the way.
// The pager still appears if a roster ever exceeds this.
const PAGE_SIZE = 10000;

function escapeHtml(value: string): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// The grid renders cell templates as HTML strings, so the complex Last-review
// and Outstanding-urgent cells reuse the same CSS classes as the old table.
function getReviewStatusGridColumns(officerLabel: string): KstDataGridColumn[] {
  return [
    {
      field: 'officer',
      name: officerLabel,
      template: (r: ReviewStatusRow) => `${escapeHtml(r.firstName)} ${escapeHtml(r.lastName)}`,
    },
    {
      field: 'group',
      name: 'Group',
      template: (r: ReviewStatusRow) =>
        `<span class="advisory-review-status-muted">${
          r.groupNames && r.groupNames.length > 0
            ? escapeHtml(r.groupNames.join(', '))
            : '—'
        }</span>`,
    },
    {
      field: 'lastReviewed',
      name: 'Last board review',
      template: (r: ReviewStatusRow) => {
        if (r.lastReviewedAt == null) {
          return '<span class="advisory-review-never">Never</span>';
        }
        const when = escapeHtml(moment(r.lastReviewedAt).format('MMM D, h:mm A'));
        return r.stale
          ? `<span class="advisory-review-stale" title="Advisories have been added or edited since this review. The board has not been reviewed since its latest change.">${when} · before latest board change</span>`
          : `<span class="advisory-review-ok">${when} ✓</span>`;
      },
    },
    {
      field: 'outstanding',
      name: 'Urgent acknowledgments outstanding',
      template: (r: ReviewStatusRow) => {
        if (!r.outstandingUrgent || r.outstandingUrgent.length === 0) {
          return '<span class="advisory-review-status-muted">—</span>';
        }
        const items = r.outstandingUrgent
          .map(
            (o) =>
              `<span class="advisory-review-outstanding__item">#${escapeHtml(String(o.displayId))} — ${escapeHtml(o.subject)}</span>`
          )
          .join('');
        return `<span class="advisory-review-outstanding">${items}</span>`;
      },
    },
  ];
}

export const AdvisoryReviewStatusTab = memo(
  forwardRef<AdvisoryReviewStatusTabHandle, AdvisoryReviewStatusTabProps>(function AdvisoryReviewStatusTab(
    { isVisible }: AdvisoryReviewStatusTabProps,
    ref
  ) {
    const { theme } = useTheme();
    const officerLabel = useMemo(() => getOfficerLabel(), []);
    const officersLabel = useMemo(() => getOfficerPluralLabel(), []);
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<ReviewStatusRow[]>([]);
    const [urgentAdvisories, setUrgentAdvisories] = useState<UrgentAdvisorySummary[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const hasLoadedRef = useRef(false);

    // Acknowledgment drill-down modal (who acknowledged an urgent advisory, and
    // when). Reuses AdvisoryAckNamesModal exactly as the operational board does.
    const [ackNamesOpen, setAckNamesOpen] = useState(false);
    const [ackNamesLoading, setAckNamesLoading] = useState(false);
    const [ackNamesSubject, setAckNamesSubject] = useState('');
    const [ackNames, setAckNames] = useState<any[]>([]);
    const [ackNamesError, setAckNamesError] = useState(false);

    const columns = useMemo(() => getReviewStatusGridColumns(officerLabel), [officerLabel]);

    const load = useCallback(() => {
      setLoading(true);
      api.getAdvisoryReviewStatus(
        (result: any) => {
          const list = Array.isArray(result)
            ? result
            : result && Array.isArray(result.reviewStatus)
              ? result.reviewStatus
              : [];
          setRows(list);
          setUrgentAdvisories(
            result && Array.isArray(result.urgentAdvisories) ? result.urgentAdvisories : []
          );
          setCurrentPage(1);
          setLoading(false);
        },
        () => {
          App.showPopupNotification('Error getting advisory review status.', 'error');
          setLoading(false);
        }
      );
    }, []);

    const openAckNames = useCallback((advisory: UrgentAdvisorySummary) => {
      setAckNamesSubject(advisory.subject);
      setAckNames([]);
      setAckNamesError(false);
      setAckNamesLoading(true);
      setAckNamesOpen(true);
      api.getAdvisoryAcknowledgments(
        advisory.advisoryId,
        (result: any) => {
          setAckNames(
            Array.isArray(result)
              ? result
              : result && result.acknowledgments
                ? result.acknowledgments
                : []
          );
          setAckNamesError(false);
          setAckNamesLoading(false);
        },
        () => {
          setAckNames([]);
          setAckNamesError(true);
          setAckNamesLoading(false);
        }
      );
    }, []);

    const closeAckNames = useCallback(() => {
      setAckNamesOpen(false);
      setAckNames([]);
    }, []);

    useImperativeHandle(ref, () => ({ refresh: () => load() }), [load]);

    // Load on first visibility.
    useEffect(() => {
      if (isVisible && !hasLoadedRef.current) {
        hasLoadedRef.current = true;
        load();
      }
    }, [isVisible, load]);

    // The API returns every officer at once, so page client-side.
    const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));

    const goToPage = useCallback(
      (page: number) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
      },
      [totalPages]
    );

    const gridRows: KstDataGridRows = useMemo(
      () => ({ entries: rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE) }),
      [rows, currentPage]
    );

    const gridPager: KstDataGridPager = useMemo(
      () => ({
        curPage: currentPage,
        numPages: totalPages,
        records: rows.length,
        isVisible: totalPages > 1,
        pageSize: PAGE_SIZE,
      }),
      [currentPage, totalPages, rows.length]
    );

    return (
      <div style={{ display: isVisible ? undefined : 'none' }}>
        <AdvisoryAckNamesModal
          open={ackNamesOpen}
          loading={ackNamesLoading}
          subject={ackNamesSubject}
          names={ackNames}
          error={ackNamesError}
          theme={theme}
          onClose={closeAckNames}
        />
        <div className="kst-results-panel">
          <div className="kst-results-body">
            <div className="advisory-review-explain">
              <i className="fa fa-info-circle"></i>
              <span>
                Board review and urgent acknowledgment are tracked separately. A board
                review confirms the {officerLabel.toLowerCase()} reviewed the advisory
                board, typically at shift start. A review made before the most recent
                advisory was added or edited is marked &ldquo;before latest board
                change&rdquo; until the {officerLabel.toLowerCase()} reviews the board
                again. Urgent advisories must be acknowledged individually and remain
                listed here until that {officerLabel.toLowerCase()} acknowledges each
                one. Acknowledgments remain in effect until an advisory
                is edited; editing an advisory prompts each {officerLabel.toLowerCase()} to
                acknowledge it again.
              </span>
            </div>
            <div className="advisory-review-urgent">
              <div className="advisory-review-urgent__heading">Urgent advisories</div>
              {urgentAdvisories.length === 0 ? (
                <div className="advisory-review-urgent__empty">
                  No urgent advisories currently require acknowledgment.
                </div>
              ) : (
                <div className="advisory-review-urgent__list">
                  {urgentAdvisories.map((a) => (
                    <div key={a.advisoryId} className="advisory-review-urgent__row">
                      <span className="advisory-review-urgent__subject">
                        #{a.displayId} — {a.subject}
                      </span>
                      <span className="advisory-review-urgent__count">
                        {a.acknowledgedCount} of {a.rosterCount} acknowledged
                      </span>
                      <button
                        type="button"
                        className="advisory-review-urgent__viewwho"
                        onClick={() => openAckNames(a)}
                      >
                        View Acknowledgments
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {loading && rows.length === 0 ? (
              <div className="loading-state"><div className="loading-spinner"></div></div>
            ) : !loading && rows.length === 0 ? (
              <div className="advisory-review-status-empty">
                No {officersLabel.toLowerCase()} hold the advisory read permission.
              </div>
            ) : (
              <KstDataGrid
                columns={columns}
                rows={gridRows}
                pager={gridPager}
                showcheckbox={false}
                showheader={false}
                mobiletitlefield="officer"
                loading={loading}
                onGoToPage={goToPage}
              />
            )}
          </div>
        </div>
      </div>
    );
  })
);

export default AdvisoryReviewStatusTab;
